import { Currency, PropertyType } from "src/types";
import { formatCurrency } from "./components/templates/design1/RentPage1";
import { Config } from "src/types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(utc);
dayjs.extend(relativeTime);
// Generates slug like "45-man-commits-suicide"
export function generateSlugWithId(id: number, title: string): string {
  const slugifiedTitle = title
    ?.toString()
    ?.toLowerCase()
    ?.trim()
    ?.replace(/\s+/g, "-") // spaces → hyphens
    ?.replace(/[^\w\-]+/g, "") // remove non-word chars except hyphen
    ?.replace(/\-\-+/g, "-"); // multiple hyphens → single hyphen
  return `${id}-${slugifiedTitle}`;
}

// Extracts the number ID from slug string like "45-man-commits-suicide"
export function extractIdFromSlug(slug: string): number {
  const firstDashIndex = slug?.indexOf("-");
  if (firstDashIndex === -1) {
    return 1;
    throw new Error("Invalid slug format: no dash found");
  }
  const idPart = slug?.substring(0, firstDashIndex);
  const id = Number(idPart);
  if (!Number.isInteger(id) || id < 0) {
    return 1;
    throw new Error("Invalid ID in slug: not a valid positive integer");
  }

  return id;
}

// Example usage:

export const newTenantsFields = [
  { label: "Inspection Fee", key: "inspectionFee", format: true },
  { label: "Rent Fee", key: "quotePrice", format: true },
  { label: "Security Fee", key: "security_fee", format: true },
  { label: "Management Fee", key: "management_fee", format: true },
  { label: "Service Charge", key: "serviceCharge", format: true },
  {
    label: "Refundable Caution Fee",
    key: "refundableCautionFee",
    format: true,
  },
  {
    label: "Non-Refundable Legal Fee",
    key: "nonRefundableLegalFees",
    format: true,
  },
  { label: "Non-Refundable Agency Fee", key: "agencyFee", format: true },
  { label: "VAT", key: "vat_amount", format: true },
  { label: "Other Fee", key: "otherFee", format: true },
  { label: "Total Package", key: "price", format: true },
  { label: "Price Condition", key: "priceCondition", format: false },
  { label: "Rent Period", key: "renew_fee_period", format: false },
];

export const renewalTenantsFields = [
  { label: "Rent Fee", key: "renew_fee_amount", format: true },
  { label: "Service Charge", key: "renew_service_charge", format: true },
  { label: "Security Fee", key: "security_fee", format: true },
  { label: "Management Fee", key: "management_fee", format: true },
  { label: "VAT", key: "renew_vat_amount", format: true },
  { label: "Other Fee", key: "renew_other_charge", format: true },
  { label: "Total Package", key: "renew_total_package", format: true },
  { label: "Price Condition", key: "priceCondition", format: false },
  { label: "Rent Period", key: "renew_fee_period", format: false },
];

export function formatTenantFields(
  property: PropertyType,
  currency: Currency,
  fields: { label: string; key: keyof PropertyType | string; format: boolean }[]
) {
  return fields.reduce((acc, { label, key, format }) => {
    const value = property[key as keyof PropertyType];
    acc[label] = format
      ? formatCurrency(currency, value as string)
      : (value as string) || "";
    return acc;
  }, {} as Record<string, string>);
}

export function formatPropertyInfo(property: PropertyType) {

  const fallback = (
    val: string | number | boolean | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    def: any = "--"
  ) => val ?? def;
  return {
    "Property Title": fallback(property.secondTitle),
    "Unit Name/Number": fallback(property.unit_name),
    Category: fallback(property.category),
    Preference: fallback(property.unitPreference),
    "Unit Type": fallback(property.newType),
    VAT: fallback(property.vat),
    "Unit Sub Type": fallback(property.unitSubtype),
    "Total Area": property.size
      ? `${property.size.toLocaleString()} sqm`
      : "--",
    Bathroom: fallback(property.bathroom),
    Bedroom: fallback(property.bedroom),
    Toilet: fallback(property.toilet),
    "Total Unit": fallback(property.total_units, 0),
    "Available Unit": fallback(property.availableUnits, 0),
    "Total Views": fallback(property.totalViews),
    Bookmarks: fallback(property.bookMarks),
    "Added Date": property.addedDate
      ? dayjs(property.addedDate).format("DD/MM/YYYY")
      : "--",
    "Last Updated": property.lastUpdated
      ? dayjs.utc(property.lastUpdated).local().fromNow()
      : "--",
  };
}

export const PropertyFaq = {
  questions: [
    {
      question: "Refund Policy",
      answer: "You can request a refund within 7 days.",
    },
    {
      question: "Property Facility",
      answer: "Facilities include WiFi, Pool, and Gym.",
    },
    {
      question: "Other Rules",
      answer: "No smoking inside the premises.",
    },
  ],
};

export function generateSchemas(config: Config, location: string) {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    name: config.companyInfo?.name || config.metadata?.siteName,
    alternateName: config.metadata?.siteName,
    url: `https://${location}`,
    logo: {
      "@type": "ImageObject",
      url: config.companyInfo?.company_logo,
      width: 800,
      height: 600,
    },
    description: config.metadata?.description,
    image: config.companyInfo?.company_logo,
    address: {
      "@type": "PostalAddress",
      streetAddress: config.companyInfo?.full_address,
      addressCountry: "NG",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: config.companyInfo?.phone_number,
      contactType: "Customer Service",
      email: config.companyInfo?.email,
      availableLanguage: ["English"],
    },
    areaServed: {
      "@type": "Country",
      name: "Nigeria",
    },
    sameAs: [
      config.companyInfo?.facebook,
      config.companyInfo?.x,
      config.companyInfo?.instagram,
      config.companyInfo?.linkedin,
    ].filter(Boolean),
    priceRange: "₦₦₦",
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: config.metadata?.siteName,
    url: `https://${location}`,
    description: config.metadata?.description,
    publisher: {
      "@type": "RealEstateAgent",
      name: config.companyInfo?.name,
      logo: {
        "@type": "ImageObject",
        url: config.companyInfo?.company_logo,
      },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `https://${location}`,
      },
    ],
  };

  return { organizationSchema, websiteSchema, breadcrumbSchema };
}
